"use client";
import { type FieldValues } from "react-hook-form";
import ErrorMessage from "@/components/common/formElements/controlled/ErrorMessage";
import {
  useDateFieldController,
  type UseDateFieldControllerProps,
} from "@/components/common/formElements/hooks/useDateFieldController";
import DatePicker, {
  type DatePickerProps,
} from "@/components/common/formElements/library/DatePicker";
import { convertISOStringToDate } from "@/components/common/formElements/utils/dates";

type DatePickerPropsWithControllerProps<TFieldValues extends FieldValues> =
  UseDateFieldControllerProps<TFieldValues> &
    Omit<
      DatePickerProps,
      | "name"
      | "value"
      | "defaultValue"
      | "disabled"
      | "onChange"
      | "onBlur"
      | "required"
      | "error"
    > & {
      fullWidth?: boolean;
    };

export default function DateField<TFieldValues extends FieldValues>(
  props: DatePickerPropsWithControllerProps<TFieldValues>,
) {
  const {
    name,
    onChange,
    onBlur,
    helperText,
    rules,
    shouldUnregister,
    disabled,
    fullWidth,
    ISOFormat,
    tooltip,
    ...restDateFieldProps
  } = props;

  const {
    field,
    required,
    onBlurField,
    onChangeField,
    formState: { errors },
    fieldState: { invalid: error },
  } = useDateFieldController({
    name,
    onChange,
    onBlur,
    rules,
    shouldUnregister,
    disabled,
    ISOFormat,
  });

  return (
    <DatePicker
      name={field.name}
      inputRef={field.ref}
      disabled={field.disabled}
      value={convertISOStringToDate(field.value as string | null)}
      onChange={onChangeField}
      error={error}
      required={required}
      fullWidth={fullWidth}
      helperText={
        error ? <ErrorMessage name={name} errors={errors} /> : helperText
      }
      tooltip={tooltip}
      onBlurField={onBlurField}
      {...restDateFieldProps}
    />
  );
}
